/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: FeaturePage.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-FEATURES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FeaturePage - Frontend UI module: FeaturePage.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-28 - UC-020: чек-бокс архива, группы списка.
 * LAST_CHANGE: 2026-07-14 - UX: заголовок в стиле портфеля, skeleton при загрузке.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блоки/логи для логики.
 * END_CHANGE_SUMMARY
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getFeatures,
  createFeature,
  updateFeature,
  archiveFeature,
  deleteFeature,
  reorderFeatures,
} from '../../api/featureApi';
import { getProjects } from '../../api/projectApi';
import { getProjectRoles } from '../../api/projectRoleApi';
import Feature from '../../models/Feature';
import Project from '../../models/Project';
import ProjectRole from '../../models/ProjectRole';
import FeatureList from './components/FeatureList';
import CreateFeatureDialog from './components/CreateFeatureDialog';
import EditFeatureDialog from './components/EditFeatureDialog';
import DeleteFeatureDialog from './components/DeleteFeatureDialog';
import ArchiveFeatureDialog from './components/ArchiveFeatureDialog';

const displayFont = '"Syne", "Inter", sans-serif';

function FeatureListSkeleton() {
  return (
    <Box sx={{ borderRadius: 1, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={40} />
      {[0, 1, 2].map((key) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
          <Skeleton variant="rounded" width={28} height={28} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="50%" height={20} />
            <Skeleton width="30%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* START_METHOD_FeaturePage */
/* START_CONTRACT: FeaturePage
 * PURPOSE: Страница задач проекта: список, создание, редактирование, архивация, удаление, изменение порядка.
 * INPUTS: {} (projectId из useParams).
 * OUTPUTS: { JSX.Element - страница с списком и диалогами }.
 * SIDE_EFFECTS: HTTP getProjects/getFeatures/getProjectRoles/create/update/archive/delete/reorderFeatures; навигация.
 * LINKS: M-FE-FEATURES, M-FE-API
 * END_CONTRACT: FeaturePage
 */
export default function FeaturePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const numericProjectId = Number(projectId);

  const [project, setProject] = useState(null);
  const [projectRoles, setProjectRoles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingFeature, setDeletingFeature] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archivingFeature, setArchivingFeature] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  /* START_METHOD_fetchData */
  /* START_CONTRACT: fetchData
   * PURPOSE: Загрузить проект, задачи и роли; обработать отсутствие проекта и ошибки.
   * INPUTS: { signal?: AbortSignal }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP getProjects/getFeatures/getProjectRoles; setState.
   * LINKS: M-FE-FEATURES
   * END_CONTRACT: fetchData
   */
  const fetchData = useCallback(async (signal) => {
    if (!numericProjectId) {
      /* START_BLOCK_INVALID_ID */
      setError('Некорректный идентификатор проекта');
      setLoading(false);
      return;
      /* END_BLOCK_INVALID_ID */
    }

    try {
      const [projectsRes, featuresRes, rolesRes] = await Promise.all([
        getProjects(signal),
        getFeatures(numericProjectId, signal),
        getProjectRoles(numericProjectId, signal),
      ]);

      const foundProject = projectsRes.data
        .map(Project.fromResponse)
        .find((p) => p.id === numericProjectId);

      if (!foundProject) {
        /* START_BLOCK_PROJECT_NOT_FOUND */
        setError('Проект не найден');
        return;
        /* END_BLOCK_PROJECT_NOT_FOUND */
      }

      /* START_BLOCK_LOAD_OK */
      setProject(foundProject);
      setProjectRoles(rolesRes.data.map(ProjectRole.fromResponse));
      setFeatures(featuresRes.data.map(Feature.fromResponse));
      setError('');
      /* END_BLOCK_LOAD_OK */
    } catch (err) {
      /* START_BLOCK_LOAD_FAIL */
      if (signal?.aborted) return;
      console.warn('[FeaturePage][fetchData][LOAD_FAIL] features load failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка загрузки задач');
      /* END_BLOCK_LOAD_FAIL */
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [numericProjectId]);
  /* END_METHOD_fetchData */

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const refetch = () => fetchData();

  /* START_METHOD_handleCreate */
  /* START_CONTRACT: handleCreate
   * PURPOSE: Создать задачу и обновить данные.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP createFeature; refetch.
   * LINKS: M-FE-FEATURES
   * END_CONTRACT: handleCreate
   */
  const handleCreate = async (payload) => {
    await createFeature(numericProjectId, payload);
    await refetch();
  };
  /* END_METHOD_handleCreate */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Обновить задачу по id и обновить данные.
   * INPUTS: { id: number, payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateFeature; refetch.
   * LINKS: M-FE-FEATURES
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (id, payload) => {
    await updateFeature(numericProjectId, id, payload);
    await refetch();
  };
  /* END_METHOD_handleUpdate */

  /* START_METHOD_handleArchive */
  /* START_CONTRACT: handleArchive
   * PURPOSE: Архивировать задачу по id и обновить данные.
   * INPUTS: { id: number }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP archiveFeature; refetch.
   * LINKS: M-FE-FEATURES
   * END_CONTRACT: handleArchive
   */
  const handleArchive = async (id) => {
    await archiveFeature(numericProjectId, id);
    await refetch();
  };
  /* END_METHOD_handleArchive */

  /* START_METHOD_handleDelete */
  /* START_CONTRACT: handleDelete
   * PURPOSE: Удалить задачу по id и обновить данные.
   * INPUTS: { id: number }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP deleteFeature; refetch.
   * LINKS: M-FE-FEATURES
   * END_CONTRACT: handleDelete
   */
  const handleDelete = async (id) => {
    await deleteFeature(numericProjectId, id);
    await refetch();
  };
  /* END_METHOD_handleDelete */

  /* START_METHOD_handleReorder */
  /* START_CONTRACT: handleReorder
   * PURPOSE: Оптимистично применить новый порядок задач и сохранить его; при ошибке откатить.
   * INPUTS: { reorderedFeatures: Feature[] }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP reorderFeatures; setFeatures/setError.
   * LINKS: M-FE-FEATURES
   * END_CONTRACT: handleReorder
   */
  const handleReorder = async (reorderedFeatures) => {
    const previousFeatures = features;
    setFeatures(reorderedFeatures);
    setError('');

    try {
      const response = await reorderFeatures(
        numericProjectId,
        reorderedFeatures.map((feature) => feature.id),
      );
      /* START_BLOCK_REORDER_OK */
      setFeatures(response.data.map(Feature.fromResponse));
      /* END_BLOCK_REORDER_OK */
    } catch (err) {
      /* START_BLOCK_REORDER_FAIL */
      console.warn('[FeaturePage][handleReorder][REORDER_FAIL] reorder failed, rolling back', {
        status: err.response?.status,
      });
      setFeatures(previousFeatures);
      setError(err.response?.data?.message || 'Ошибка сохранения порядка задач');
      /* END_BLOCK_REORDER_FAIL */
    }
  };
  /* END_METHOD_handleReorder */

  if (loading) {
    return (
      <Box>
        <Skeleton width={120} height={32} sx={{ mb: 2 }} />
        <Skeleton width="55%" height={40} sx={{ mb: 3 }} />
        <FeatureListSkeleton />
      </Box>
    );
  }

  if (error && !project) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          К проектам
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        size="small"
        onClick={() => navigate('/projects')}
        sx={{ mb: 2 }}
      >
        К проектам
      </Button>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          pb: 3,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.12em',
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            Бэклог
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontFamily: displayFont,
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
              }}
            >
              {project?.name}
            </Typography>
            {features.length > 0 && (
              <Chip
                label={`${features.length} ${features.length === 1 ? 'задача' : features.length < 5 ? 'задачи' : 'задач'}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: (theme) => theme.palette.primary.main + '14',
                  color: 'primary.main',
                  border: 'none',
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 480 }}>
            Порядок задач определяет очередь на таймлайне. Перетаскивайте строки, чтобы изменить приоритет.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
        >
          Новая задача
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormControlLabel
        control={(
          <Checkbox
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
            size="small"
          />
        )}
        label="Отображать архивные задачи"
        sx={{ mb: 1.5, ml: 0, userSelect: 'none' }}
      />

      <FeatureList
        features={features}
        showArchived={showArchived}
        onReorder={handleReorder}
        onEdit={(feature) => {
          setEditingFeature(feature);
          setEditOpen(true);
        }}
        onArchive={(feature) => {
          setArchivingFeature(feature);
          setArchiveOpen(true);
        }}
        onDelete={(feature) => {
          setDeletingFeature(feature);
          setDeleteOpen(true);
        }}
      />

      <CreateFeatureDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        projectRoles={projectRoles}
      />

      <EditFeatureDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingFeature(null);
        }}
        onUpdate={handleUpdate}
        feature={editingFeature}
        projectRoles={projectRoles}
      />

      <ArchiveFeatureDialog
        open={archiveOpen}
        onClose={() => {
          setArchiveOpen(false);
          setArchivingFeature(null);
        }}
        onConfirm={handleArchive}
        feature={archivingFeature}
      />

      <DeleteFeatureDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingFeature(null);
        }}
        onConfirm={handleDelete}
        feature={deletingFeature}
      />
    </Box>
  );
}
/* END_METHOD_FeaturePage */
